import { AssessmentResult } from '../../types/assessment';
import { ratingClass, displayRating, scoreClass, scoreBadgeLabel } from './scoreHelpers';
import { computeScores } from './computeScores';
import TiCard from '../common/TiCard';

interface Props {
  result: AssessmentResult;
}

/** Helper: pill class based on score percentage */
function pillClass(score: number, max: number): string {
  const pct = (score / max) * 100;
  if (pct >= 70) return 'pill-green';
  if (pct >= 45) return 'pill-orange';
  return 'pill-red';
}

/** Helper: pill class for deduction magnitude (0 = green, small = orange, large = red) */
function dedPillClass(absDeduction: number): string {
  if (absDeduction === 0) return 'pill-green';
  if (absDeduction <= 5) return 'pill-orange';
  return 'pill-red';
}

/** Helper: format a deduction as −N or 0 */
function formatDed(absDeduction: number): string {
  if (absDeduction === 0) return '0';
  return `−${absDeduction}`;
}

export default function FullAnalysisCard({ result }: Props) {
  const { article, scores, knockout, rating, is_opinion, quality_breakdown: qb, veracity_breakdown: vb, valence_breakdown: vlb, manipulation_breakdown: mb, confidence: conf } = result;
  const rc = ratingClass(rating);
  const dr = displayRating(rating);

  const c = computeScores(result);

  return (
    <TiCard label="Full analytical transparency — step by step methodology" animate fullHeight>

      {/* Intro */}
      <div className="ti-card" style={{ marginBottom: 10 }}>
        <p style={{ fontSize: 13, color: '#5f5e5a', lineHeight: 1.7, margin: 0 }}>
          This section documents every analytical decision made during this assessment with direct evidence from the article text, allowing any reader to verify, challenge, or reproduce the scoring independently. The Trust Index methodology is applied in full, step by step.
        </p>
      </div>

      {/* Step 1: Content retrieval & article identification */}
      <div className="ti-card" style={{ marginBottom: 10 }}>
        <div className="step-header">
          <div className="step-num">1</div>
          <div>
            <div className="step-title">Content retrieval &amp; article identification</div>
            <div className="step-subtitle">Source confirmed before analysis begins</div>
          </div>
        </div>
        <div className="step-body">
          <div className="evidence-block">
            <strong>Title:</strong> {article.title}<br />
            <strong>Publisher:</strong> {article.publisher}<br />
            <strong>Author:</strong> {article.author ?? 'Not identified'}<br />
            {article.url && (
              <>
                <strong>URL:</strong>{' '}
                <a href={article.url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                  {article.url}
                </a>
                <br />
              </>
            )}
            <strong>Publication date:</strong> {article.date ?? 'Not available'}<br />
            <strong>Format:</strong> {article.format ?? article.content_type}{article.word_count ? ` (~${article.word_count} words)` : ''}<br />
            <strong>Topic category:</strong> {article.topic_category ?? 'General'}<br />
            {article.multimedia_note && <><strong>Multimedia:</strong> {article.multimedia_note}<br /></>}
          </div>
          {article.retrieval_note && <p>{article.retrieval_note}</p>}
        </div>
      </div>

      {/* Step 2: Publisher research */}
      <div className="ti-card" style={{ marginBottom: 10 }}>
        <div className="step-header">
          <div className="step-num">2</div>
          <div>
            <div className="step-title">Publisher research</div>
            <div className="step-subtitle">Independent credibility assessment before article scoring</div>
          </div>
        </div>
        <div className="step-body">
          <div className="evidence-block">
            <strong>{article.publisher} institutional profile:</strong><br />
            {vb.mbfc_rating && <>— MBFC rating: {vb.mbfc_rating}<br /></>}
            {vb.newsguard_score && <>— NewsGuard: {vb.newsguard_score}<br /></>}
            {vb.ad_fontes_score && <>— Ad Fontes: {vb.ad_fontes_score}<br /></>}
            {vb.awards_found && <>— Awards: {vb.awards_found}<br /></>}
            {vb.publisher_research && <>— {vb.publisher_research}<br /></>}
          </div>
          <p>
            <strong>Publisher adjustment decision:</strong> {vb.adjustment_rationale}{' '}
            <span className={`score-pill ${scores.veracity_adjustment >= 0 ? (scores.veracity_adjustment > 0 ? 'pill-green' : 'pill-orange') : 'pill-red'}`}>
              {scores.veracity_adjustment >= 0 ? '+' : ''}{scores.veracity_adjustment} applied
            </span>
          </p>
        </div>
      </div>

      {/* Step 3: Content analysis checks */}
      <div className="ti-card" style={{ marginBottom: 10 }}>
        <div className="step-header">
          <div className="step-num">3</div>
          <div>
            <div className="step-title">Content analysis checks</div>
            <div className="step-subtitle">AI/synthetic detection &middot; temporal check &middot; multimedia review</div>
          </div>
        </div>
        <div className="step-body">
          {result.content_checks ? (
            <>
              <p><strong>AI / synthetic content check.</strong> {result.content_checks.ai_check}</p>
              <p><strong>Temporal check.</strong> {result.content_checks.temporal_check}</p>
              <p><strong>Multimedia check.</strong> {result.content_checks.multimedia_check}</p>
            </>
          ) : (
            <>
              <p><strong>AI / synthetic content check.</strong>{' '}
                <span className={`score-pill ${!knockout.rule3_triggered ? 'pill-green' : 'pill-red'}`}>
                  {knockout.rule3_triggered ? 'Synthetic content detected' : 'No synthetic content detected'}
                </span>
              </p>
              <p><strong>Temporal check.</strong>{' '}
                <span className={`score-pill ${qb.temporal_penalty === 0 ? 'pill-green' : 'pill-orange'}`}>
                  {qb.temporal_penalty === 0 ? 'No penalty' : `Penalty: ${qb.temporal_penalty} pts`}
                </span>
              </p>
              <p><strong>Content authenticity:</strong> AI disclosure: {qb.authenticity_ai_disclosure ?? 'N/A'}, Provenance: {qb.authenticity_provenance ?? 'N/A'}</p>
            </>
          )}
        </div>
      </div>

      {/* Step 4: Quality index — detailed assessment */}
      {!is_opinion && scores.quality != null && (
        <div className="ti-card" style={{ marginBottom: 10 }}>
          <div className="step-header">
            <div className="step-num">4</div>
            <div>
              <div className="step-title">Quality index — detailed assessment &middot; {c.qualityTotal}/100</div>
              <div className="step-subtitle">Six sub-components scored against the rubric &middot; Maximum 100 points</div>
            </div>
          </div>
          <div className="step-body">
            <p>
              <strong>4.1 Completeness &amp; Context — {qb.completeness ?? 'N/A'}/23.</strong>{' '}
              {qb.completeness_rationale || 'Evaluates 5Ws+H coverage and contextual depth.'}{' '}
              <span className={`score-pill ${pillClass(qb.completeness ?? 0, 23)}`}>{qb.completeness ?? 'N/A'} / 23</span>
            </p>
            <p>
              <strong>4.2 Balance &amp; Fairness — {qb.balance ?? 'N/A'}/18.</strong>{' '}
              {qb.balance_rationale || 'Evaluates perspectives represented and how they are framed.'}{' '}
              <span className={`score-pill ${pillClass(qb.balance ?? 0, 18)}`}>{qb.balance ?? 'N/A'} / 18</span>
            </p>
            <p>
              <strong>4.3 Depth &amp; Thoroughness — {qb.depth ?? 'N/A'}/18.</strong>{' '}
              {qb.depth_rationale || 'Evaluates original reporting, expert analysis, and investigative depth.'}{' '}
              <span className={`score-pill ${pillClass(qb.depth ?? 0, 18)}`}>{qb.depth ?? 'N/A'} / 18</span>
            </p>
            <p>
              <strong>4.4 Editorial Standards — {c.editorialTotal}/18.</strong>{' '}
              {qb.editorial_rationale || `Presentation: ${qb.editorial_presentation ?? 'N/A'}/5, Corrections: ${qb.editorial_corrections ?? 'N/A'}/8, Temporal: ${qb.editorial_temporal ?? 'N/A'}/5.`}{' '}
              <span className={`score-pill ${pillClass(c.editorialTotal, 18)}`}>{c.editorialTotal} / 18</span>
            </p>
            <p>
              <strong>4.5 Transparency &amp; Accountability — {qb.transparency ?? 'N/A'}/18.</strong>{' '}
              {qb.transparency_rationale || 'Evaluates source links, author credentials, methodology, and corrections policy.'}{' '}
              <span className={`score-pill ${pillClass(qb.transparency ?? 0, 18)}`}>{qb.transparency ?? 'N/A'} / 18</span>
            </p>
            <p>
              <strong>4.6 Content Authenticity — {c.authenticityTotal}/5.</strong>{' '}
              {qb.authenticity_rationale || 'Evaluates human vs AI authorship and provenance of claims.'}{' '}
              <span className={`score-pill ${pillClass(c.authenticityTotal, 5)}`}>{c.authenticityTotal} / 5</span>
            </p>
            <div className="calc-block">
              <div className="calc-line"><span>Completeness &amp; context</span><span className={scoreClass(qb.completeness ?? 0, 23)}>{qb.completeness ?? 'N/A'} / 23</span></div>
              <div className="calc-line"><span>Balance &amp; fairness</span><span className={scoreClass(qb.balance ?? 0, 18)}>{qb.balance ?? 'N/A'} / 18</span></div>
              <div className="calc-line"><span>Depth &amp; thoroughness</span><span className={scoreClass(qb.depth ?? 0, 18)}>{qb.depth ?? 'N/A'} / 18</span></div>
              <div className="calc-line"><span>Editorial standards ({qb.editorial_presentation ?? 0}+{qb.editorial_corrections ?? 0}+{qb.editorial_temporal ?? 0})</span><span className={scoreClass(c.editorialTotal, 18)}>{c.editorialTotal} / 18</span></div>
              <div className="calc-line"><span>Transparency &amp; accountability</span><span className={scoreClass(qb.transparency ?? 0, 18)}>{qb.transparency ?? 'N/A'} / 18</span></div>
              <div className="calc-line"><span>Content authenticity ({qb.authenticity_ai_disclosure ?? 0}+{qb.authenticity_provenance ?? 0})</span><span className={scoreClass(c.authenticityTotal, 5)}>{c.authenticityTotal} / 5</span></div>
              <div className="calc-line"><span>Temporal penalty</span><span className={qb.temporal_penalty === 0 ? 'val-green' : 'val-red'}>{qb.temporal_penalty === 0 ? '0 — content current' : `−${qb.temporal_penalty}`}</span></div>
              <div className="calc-line total"><span>Quality index total</span><span className={scoreClass(c.qualityTotal, 100)}>{c.qualityTotal} / 100 — {scoreBadgeLabel(c.qualityTotal, 100).toUpperCase()}</span></div>
            </div>
            {qb.article_type_notes && (
              <div className="methodology-note">{qb.article_type_notes}</div>
            )}
          </div>
        </div>
      )}

      {/* Step 5: Source veracity — detailed assessment */}
      <div className="ti-card" style={{ marginBottom: 10 }}>
        <div className="step-header">
          <div className="step-num">5</div>
          <div>
            <div className="step-title">Source veracity — detailed assessment &middot; {c.veracityFinal}/100</div>
            <div className="step-subtitle">Article sourcing {c.veracityArticle}/100 &middot; Publisher adjustment {scores.veracity_adjustment >= 0 ? '+' : ''}{scores.veracity_adjustment}</div>
          </div>
        </div>
        <div className="step-body">
          <p>
            <strong>5.1 Source Identification — {c.sourceId}/20.</strong>{' '}
            {vb.source_identification_rationale || `Named sources: ${vb.named_sources}/10, Attribution clarity: ${vb.attribution_clarity}/10.`}{' '}
            <span className={`score-pill ${pillClass(c.sourceId, 20)}`}>{c.sourceId} / 20</span>
          </p>
          <p>
            <strong>5.2 Source Quality &amp; Type — {c.sourceQuality}/25.</strong>{' '}
            {vb.source_quality_rationale || `Primary: ${vb.primary_sources}/10, Expert: ${vb.expert_sources}/8, Independence: ${vb.source_independence}/7.`}{' '}
            <span className={`score-pill ${pillClass(c.sourceQuality, 25)}`}>{c.sourceQuality} / 25</span>
          </p>
          <p>
            <strong>5.3 Source Framing &amp; Context — {c.sourceFraming}/25.</strong>{' '}
            {vb.source_framing_rationale || `Context: ${vb.context_provided}/10, Skepticism: ${vb.appropriate_skepticism}/8, Conflicts: ${vb.conflicting_sources}/7.`}{' '}
            <span className={`score-pill ${pillClass(c.sourceFraming, 25)}`}>{c.sourceFraming} / 25</span>
          </p>
          <p>
            <strong>5.4 Lateral Verification — {c.lateralVerification}/20.</strong>{' '}
            {vb.lateral_verification_rationale || `Multiple confirmation: ${vb.multiple_confirmation}/10, Cross-reference: ${vb.cross_reference}/10.`}{' '}
            <span className={`score-pill ${pillClass(c.lateralVerification, 20)}`}>{c.lateralVerification} / 20</span>
          </p>
          <p>
            <strong>5.5 Source Accessibility — {c.sourceAccessibility}/10.</strong>{' '}
            {vb.source_accessibility_rationale || `Links: ${vb.links}/5, Retrievability: ${vb.retrievability}/5.`}{' '}
            <span className={`score-pill ${pillClass(c.sourceAccessibility, 10)}`}>{c.sourceAccessibility} / 10</span>
          </p>
          <div className="calc-block">
            <div className="calc-line"><span>Source identification ({vb.named_sources}+{vb.attribution_clarity})</span><span className={scoreClass(c.sourceId, 20)}>{c.sourceId} / 20</span></div>
            <div className="calc-line"><span>Source quality &amp; type ({vb.primary_sources}+{vb.expert_sources}+{vb.source_independence})</span><span className={scoreClass(c.sourceQuality, 25)}>{c.sourceQuality} / 25</span></div>
            <div className="calc-line"><span>Source framing &amp; context ({vb.context_provided}+{vb.appropriate_skepticism}+{vb.conflicting_sources})</span><span className={scoreClass(c.sourceFraming, 25)}>{c.sourceFraming} / 25</span></div>
            <div className="calc-line"><span>Lateral verification ({vb.multiple_confirmation}+{vb.cross_reference})</span><span className={scoreClass(c.lateralVerification, 20)}>{c.lateralVerification} / 20</span></div>
            <div className="calc-line"><span>Source accessibility ({vb.links}+{vb.retrievability})</span><span className={scoreClass(c.sourceAccessibility, 10)}>{c.sourceAccessibility} / 10</span></div>
            <div className="calc-line total"><span>Article sourcing subtotal</span><span className={scoreClass(c.veracityArticle, 100)}>{c.veracityArticle} / 100</span></div>
            <div className="calc-line"><span>Publisher adjustment ({vb.adjustment_rationale?.split('.')[0] || 'see above'})</span><span className={scores.veracity_adjustment >= 0 ? 'val-green' : 'val-red'}>{scores.veracity_adjustment >= 0 ? '+' : ''}{scores.veracity_adjustment}</span></div>
            <div className="calc-line total"><span>Combined veracity (scaled to 100)</span><span className={scoreClass(c.veracityFinal, 100)}>{c.veracityFinal} / 100 — {scoreBadgeLabel(c.veracityFinal, 100).toUpperCase()}</span></div>
          </div>
        </div>
      </div>

      {/* Step 6: Feeling valence — detailed assessment */}
      {!is_opinion && scores.valence != null && (
        <div className="ti-card" style={{ marginBottom: 10 }}>
          <div className="step-header">
            <div className="step-num">6</div>
            <div>
              <div className="step-title">Feeling valence — detailed assessment &middot; {c.valenceTotal}/100</div>
              <div className="step-subtitle">Higher score = less emotional = better &middot; Maximum 100</div>
            </div>
          </div>
          <div className="step-body">
            <p>
              <strong>Emotional intensity ({vlb.intensity_pts ?? 'N/A'}/20).</strong>{' '}
              {vlb.intensity_rationale || (vlb.intensity_level ? `${vlb.intensity_level} band${vlb.intensity_vader_estimate != null ? `, VADER estimate ~${vlb.intensity_vader_estimate}` : ''}.` : 'Intensity assessment.')}{' '}
            </p>
            {vlb.loaded_language_examples && (
              <div className="evidence-block">
                <strong>Loaded language identified:</strong><br />
                {vlb.loaded_language_examples.split('\n').map((line, i) => (
                  <span key={i}>— {line}<br /></span>
                ))}
              </div>
            )}
            <p>Base score: <span className={`score-pill ${pillClass(vlb.intensity_pts ?? 0, 20)}`}>{vlb.intensity_pts ?? 'N/A'} / 20</span></p>
            <p>
              <strong>Emotional valence type ({vlb.valence_type_pts ?? 'N/A'}/15).</strong>{' '}
              {vlb.type_rationale || `Dominant emotion: ${vlb.dominant_emotion ?? 'N/A'}. Base: ${vlb.valence_type_base ?? 'N/A'}, Context adjustment: ${vlb.valence_type_adjustment != null ? (vlb.valence_type_adjustment >= 0 ? '+' : '') + vlb.valence_type_adjustment : 'N/A'}.`}{' '}
              <span className={`score-pill ${pillClass(vlb.valence_type_pts ?? 0, 15)}`}>{vlb.valence_type_pts ?? 'N/A'} / 15</span>
            </p>
            <p>
              <strong>Cumulative emotional load ({vlb.cumulative_load_pts ?? 'N/A'}/10).</strong>{' '}
              {vlb.load_rationale || `Average load estimate: ${vlb.avg_load_estimate ?? 'N/A'}, Distribution: ${vlb.distribution_pattern ?? 'N/A'}.`}{' '}
              <span className={`score-pill ${pillClass(vlb.cumulative_load_pts ?? 0, 10)}`}>{vlb.cumulative_load_pts ?? 'N/A'} / 10</span>
            </p>
            <p>
              <strong>Intensity amplifiers ({vlb.amplifiers_pts ?? 'N/A'}/5).</strong>{' '}
              {vlb.amplifiers_rationale || (vlb.amplifiers_detected ?? 'No amplifiers detected.')}{' '}
              <span className={`score-pill ${pillClass(vlb.amplifiers_pts ?? 0, 5)}`}>{vlb.amplifiers_pts ?? 'N/A'} / 5</span>
            </p>
            <div className="calc-block">
              <div className="calc-line"><span>Emotional intensity{vlb.intensity_vader_estimate != null ? ` (VADER ~${vlb.intensity_vader_estimate}, ${vlb.intensity_level ?? ''})` : ''}</span><span className={scoreClass(vlb.intensity_pts ?? 0, 20)}>{vlb.intensity_pts ?? 'N/A'} / 20</span></div>
              <div className="calc-line"><span>Valence type — base</span><span>{vlb.valence_type_base ?? 'N/A'}</span></div>
              {vlb.valence_type_adjustment != null && vlb.valence_type_adjustment !== 0 && (
                <div className="calc-line"><span>Valence type — context adjustment ({vlb.context_note ?? ''})</span><span>{vlb.valence_type_adjustment >= 0 ? '+' : ''}{vlb.valence_type_adjustment}</span></div>
              )}
              <div className="calc-line"><span>Valence type total</span><span className={scoreClass(vlb.valence_type_pts ?? 0, 15)}>{vlb.valence_type_pts ?? 'N/A'} / 15</span></div>
              <div className="calc-line"><span>Cumulative load{vlb.avg_load_estimate != null ? ` (avg ~${vlb.avg_load_estimate}, ${vlb.distribution_pattern ?? ''})` : ''}</span><span className={scoreClass(vlb.cumulative_load_pts ?? 0, 10)}>{vlb.cumulative_load_pts ?? 'N/A'} / 10</span></div>
              <div className="calc-line"><span>Intensity amplifiers</span><span className={scoreClass(vlb.amplifiers_pts ?? 0, 5)}>{vlb.amplifiers_pts ?? 'N/A'} / 5</span></div>
              <div className="calc-line total"><span>Subtotal</span><span>{c.valenceSubtotal50} / 50</span></div>
              {(vlb.article_type_adjustment ?? 0) !== 0 && (
                <div className="calc-line"><span>{article.content_type} article type adjustment</span><span>+{vlb.article_type_adjustment}</span></div>
              )}
              <div className="calc-line total"><span>Adjusted subtotal &times; 2</span><span className={scoreClass(c.valenceTotal, 100)}>{c.valenceAdjusted50} / 50 &times; 2 = {c.valenceTotal} / 100 — {scoreBadgeLabel(c.valenceTotal, 100).toUpperCase()}</span></div>
            </div>
            {vlb.context_note && (
              <div className="methodology-note">{vlb.context_note}</div>
            )}
          </div>
        </div>
      )}

      {/* Step 7: Manipulation index — detailed assessment */}
      <div className="ti-card" style={{ marginBottom: 10 }}>
        <div className="step-header">
          <div className="step-num">7</div>
          <div>
            <div className="step-title">Manipulation index — detailed assessment &middot; {c.manipulationTotal}/100</div>
            <div className="step-subtitle">Instance-counted &middot; 5 severity tiers &middot; category caps applied</div>
          </div>
        </div>
        <div className="step-body">
          <p>
            <strong>7.1 Manipulative language — {formatDed(Math.abs(mb.loaded_language))}.</strong>{' '}
            {mb.loaded_language_instances || 'None detected.'}{' '}
            <span className={`score-pill ${dedPillClass(Math.abs(mb.loaded_language))}`}>{formatDed(Math.abs(mb.loaded_language))}</span>
          </p>
          <p>
            <strong>7.2 Logical fallacies — {formatDed(Math.abs(mb.logical_fallacies))}.</strong>{' '}
            {mb.logical_fallacies_instances || 'None detected.'}{' '}
            <span className={`score-pill ${dedPillClass(Math.abs(mb.logical_fallacies))}`}>{formatDed(Math.abs(mb.logical_fallacies))}</span>
          </p>
          <p>
            <strong>7.3 Diversion tactics — {formatDed(Math.abs(mb.diversion))}.</strong>{' '}
            {mb.diversion_instances || 'None detected.'}{' '}
            <span className={`score-pill ${dedPillClass(Math.abs(mb.diversion))}`}>{formatDed(Math.abs(mb.diversion))}</span>
          </p>
          <p>
            <strong>7.4 Social &amp; emotional pressure — {formatDed(Math.abs(mb.social_pressure))}.</strong>{' '}
            {mb.social_pressure_instances || 'None detected.'}{' '}
            <span className={`score-pill ${dedPillClass(Math.abs(mb.social_pressure))}`}>{formatDed(Math.abs(mb.social_pressure))}</span>
          </p>
          <p>
            <strong>7.5 Cognitive bias exploitation — {formatDed(Math.abs(mb.cognitive_bias))}.</strong>{' '}
            {mb.cognitive_bias_instances || 'None detected.'}{' '}
            <span className={`score-pill ${dedPillClass(Math.abs(mb.cognitive_bias))}`}>{formatDed(Math.abs(mb.cognitive_bias))}</span>
          </p>
          <p>
            <strong>7.6 Dehumanization — {formatDed(Math.abs(mb.dehumanization))}.</strong>{' '}
            {mb.dehumanization === 0 ? 'Not detected.' : 'Detected — score drops to min(30, current) and Rule 1 flagged.'}{' '}
            <span className={`score-pill ${dedPillClass(Math.abs(mb.dehumanization))}`}>{formatDed(Math.abs(mb.dehumanization))}</span>
          </p>
          <div className="calc-block">
            <div className="calc-line"><span>Base</span><span>100</span></div>
            <div className="calc-line"><span>Manipulative language (cap −20)</span><span className={scoreClass(100 - Math.abs(mb.loaded_language), 100)}>{mb.loaded_language === 0 ? '0' : `−${Math.abs(mb.loaded_language)}`}</span></div>
            <div className="calc-line"><span>Logical fallacies (cap −15)</span><span className={scoreClass(100 - Math.abs(mb.logical_fallacies), 100)}>{mb.logical_fallacies === 0 ? '0' : `−${Math.abs(mb.logical_fallacies)}`}</span></div>
            <div className="calc-line"><span>Diversion tactics (cap −10)</span><span className={scoreClass(100 - Math.abs(mb.diversion), 100)}>{mb.diversion === 0 ? '0' : `−${Math.abs(mb.diversion)}`}</span></div>
            <div className="calc-line"><span>Social &amp; emotional pressure (cap −5)</span><span className={scoreClass(100 - Math.abs(mb.social_pressure), 100)}>{mb.social_pressure === 0 ? '0' : `−${Math.abs(mb.social_pressure)}`}</span></div>
            <div className="calc-line"><span>Cognitive bias exploitation (cap −10)</span><span className={scoreClass(100 - Math.abs(mb.cognitive_bias), 100)}>{mb.cognitive_bias === 0 ? '0' : `−${Math.abs(mb.cognitive_bias)}`}</span></div>
            <div className="calc-line"><span>Dehumanization</span><span className={scoreClass(100 - Math.abs(mb.dehumanization), 100)}>{mb.dehumanization === 0 ? '0 — not detected' : `−${Math.abs(mb.dehumanization)}`}</span></div>
            <div className="calc-line total"><span>Manipulation index total</span><span className={scoreClass(c.manipulationTotal, 100)}>{c.manipulationTotal} / 100 — {scoreBadgeLabel(c.manipulationTotal, 100).toUpperCase()}</span></div>
          </div>
          {mb.notes && (
            <div className="methodology-note">{mb.notes}</div>
          )}
        </div>
      </div>

      {/* Step 8: Knockout rules check */}
      <div className="ti-card" style={{ marginBottom: 10 }}>
        <div className="step-header">
          <div className="step-num">8</div>
          <div>
            <div className="step-title">Knockout rules check</div>
            <div className="step-subtitle">All three rules evaluated before final rating assigned</div>
          </div>
        </div>
        <div className="step-body">
          <div className="evidence-block">
            <strong>Rule 1 — Severe Manipulation (Manipulation &lt; 40):</strong><br />
            Manipulation score: {c.manipulationTotal}.{' '}
            <span className={`score-pill ${c.manipulationTotal >= 40 ? 'pill-green' : 'pill-red'}`}>
              {knockout.rule1_triggered ? 'TRIGGERED ❌ — RED override' : 'Not triggered ✅'}
            </span><br /><br />
            <strong>Rule 2 — Critical Accuracy Failure (Quality &lt; 40 OR Veracity article-level &lt; 40):</strong><br />
            Quality: {is_opinion ? 'N/A' : c.qualityTotal}. Veracity article-level: {c.veracityArticle}.{' '}
            <span className={`score-pill ${!knockout.rule2_triggered ? 'pill-green' : 'pill-red'}`}>
              {knockout.rule2_triggered ? 'TRIGGERED ❌ — max ORANGE' : 'Not triggered ✅'}
            </span><br /><br />
            <strong>Rule 3 — Synthetic Media Deception:</strong><br />
            <span className={`score-pill ${!knockout.rule3_triggered ? 'pill-green' : 'pill-red'}`}>
              {knockout.rule3_triggered ? 'TRIGGERED ❌ — RED override' : 'Not triggered ✅'}
            </span>
          </div>
          {knockout.note && <p>{knockout.note.replace(/\bYELLOW\b/g, 'ORANGE')}</p>}
        </div>
      </div>

      {/* Step 9: Final calculation */}
      <div className="ti-card" style={{ marginBottom: 10 }}>
        <div className="step-header">
          <div className="step-num">9</div>
          <div>
            <div className="step-title">Final calculation</div>
            <div className="step-subtitle">60/40 weighted formula &middot; Veracity scaled to 0–100</div>
          </div>
        </div>
        <div className="step-body">
          <div className="calc-block">
            {!is_opinion ? (
              <>
                <div className="calc-line">
                  <span>Accuracy: (Quality {c.qualityTotal} + Veracity {c.veracityFinal}) / 2 &times; 0.60</span>
                  <span className={scoreClass(c.accuracyWeighted, 60)}>{c.accuracyWeighted.toFixed(2)} / 60</span>
                </div>
                <div className="calc-line">
                  <span>Emotional: (Valence {c.valenceTotal} + Manipulation {c.manipulationTotal}) / 2 &times; 0.40</span>
                  <span className={scoreClass(c.emotionalWeighted, 40)}>{c.emotionalWeighted.toFixed(2)} / 40</span>
                </div>
                <div className="calc-line">
                  <span>Base score ({c.accuracyWeighted.toFixed(2)} + {c.emotionalWeighted.toFixed(2)})</span>
                  <span>{c.baseScore.toFixed(2)}</span>
                </div>
              </>
            ) : (
              <div className="calc-line">
                <span>Partial: (Veracity {c.veracityFinal} + Manipulation {c.manipulationTotal}) / 2</span>
                <span>{c.finalScore}</span>
              </div>
            )}
            <div className="calc-line">
              <span>Clamped to 0–100, rounded</span>
              <span>{c.finalScore}</span>
            </div>
            <div className="calc-line total">
              <span>Final score</span>
              <span className={`val-${rc}`}>{c.finalScore} / 100 &middot; {dr}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Step 10: Analytical confidence & limitations */}
      <div className="ti-card">
        <div className="step-header">
          <div className="step-num">10</div>
          <div>
            <div className="step-title">Analytical confidence &amp; limitations</div>
            <div className="step-subtitle">Where uncertainty exists in this assessment</div>
          </div>
        </div>
        <div className="step-body">
          <div className="evidence-block">
            <strong>Check 1 — Publisher data: {conf.publisher.level}.</strong> {conf.publisher.detail}<br /><br />
            <strong>Check 2 — Content access: {conf.access.level}.</strong> {conf.access.detail}<br /><br />
            <strong>Check 3 — Score threshold: {conf.threshold.level}.</strong> {conf.threshold.detail}<br /><br />
            <strong>Check 4 — Knockout proximity: {conf.knockout.level}.</strong> {conf.knockout.detail}
          </div>
          {result.analytical_confidence ? (
            <>
              <div className="evidence-block">
                <strong>Highest-variance scoring decision:</strong> {result.analytical_confidence.highest_variance}<br /><br />
                <strong>Second variance area:</strong> {result.analytical_confidence.second_variance}<br /><br />
                <strong>Score sensitivity:</strong> {result.analytical_confidence.score_sensitivity}
              </div>
              <p><strong>What this analysis cannot determine:</strong> {result.analytical_confidence.limitations}</p>
            </>
          ) : (
            <p><strong>Score sensitivity:</strong> Final score {c.finalScore} is {conf.threshold.distance_from_threshold} points from the nearest threshold ({conf.threshold.nearest_threshold}).{conf.threshold.swing_criterion ? ` Swing criterion: ${conf.threshold.swing_criterion}` : ''}</p>
          )}
        </div>
      </div>

      {/* Web sources */}
      {result.web_sources && result.web_sources!.length > 0 && (
        <div className="ti-card" style={{ marginTop: 10 }}>
          <div className="section-label">Sources consulted ({result.web_sources!.length})</div>
          <ol className="web-sources-list">
            {result.web_sources!.map((source, i) => (
              <li key={i}>
                <span className="web-source-num">{i}</span>
                <div>
                  <a href={source.url} target="_blank" rel="noopener noreferrer" className="web-source-link">
                    <span className="web-source-title">{source.title}</span>
                  </a>
                  <div className="web-source-url">{source.url}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </TiCard>
  );
}
